import type { ApiError } from './errors.ts';
import type { ErrorCode } from './generated/models.ts';

/**
 * One place where an `ApiError.code` becomes text a person reads.
 *
 * Screens do not write their own copy for a refusal. Eighteen screens wording
 * the same refusal eighteen ways is eighteen chances to say something the API
 * deliberately does not say — and the wording of an authentication failure is
 * not a presentation detail, it is the disclosure boundary itself.
 *
 * The map lives with the client package rather than with a screen because that
 * is where `code` is defined and where the API's registry already sits: a
 * message and its retryability are answers about the same code, and splitting
 * them across packages is how one gets updated without the other.
 */

/**
 * A code this app deliberately leaves to the generic fallback, and why.
 *
 * **The reason is an argument rather than a comment, and that is the whole
 * design.** A bare marker is a comment away from being a dumping ground: within
 * two epics it reads as *"somebody decided"* when it means *"somebody
 * deferred"*, and the door this constant exists to close is back, spelled
 * differently. Both live examples of that family sit in these two repos — the
 * API's `#171` idempotency exemption, and our own `BY_SCOPE` reconciler map,
 * whose premise ("there are two write scopes") was reasonable when written and
 * was never revisited. Neither was wrong when it was made; both stopped being
 * decisions and nobody could tell.
 *
 * So the reason cannot be omitted without failing to compile, and
 * `error-messages.test.ts` asserts it says something — a reviewer must be able
 * to tell a decision from a deferral without asking the author.
 *
 * What it means for the person at the screen: `messageForErrorCode` renders
 * `REFUSED_WITH_CODE`, which states plainly that the server refused and quotes
 * the code to read down the phone. For a fault that is the honest answer; for a
 * refusal that states a rule it is not, and such a code wants a sentence here
 * instead.
 */
const DELIBERATELY_GENERIC = (reason: string): GenericFallback => ({ genericBecause: reason });

interface GenericFallback {
  readonly genericBecause: string;
}

/**
 * What each code in the registry says to the person in front of it.
 *
 * **Total over `ErrorCode`, as of #135, and that is the point of the ticket.**
 * This was a `Partial` record for four epics: a code with no row compiled,
 * rendered and shipped, and the only thing that noticed was the person reading
 * "report it" about a refusal they could have fixed themselves. Thirty-three of
 * seventy-three codes were in that state when the ticket was written, and every
 * ticket since Day Close had carried the same caveat in the same words. Five
 * reminders is one missing gate restated five times.
 *
 * `satisfies Record<ErrorCode, …>` is that gate, and it is the property
 * `errorRetryability` next door has always had: a code added upstream now fails
 * `pnpm typecheck` until somebody decides what it says, exactly as one added
 * upstream already fails until somebody classifies its retryability. **This is a
 * deliberate cost.** Every future regeneration that widens the registry stops
 * compiling until the copy is written or the fallback is chosen in writing —
 * which is the only arrangement under which "we forgot" is a thing the build can
 * say out loud.
 *
 * An entry is either a sentence a person reads or a `DELIBERATELY_GENERIC`
 * carrying the argument for leaving it alone. There is no third option and no
 * way to write neither.
 *
 * Keyed by `ErrorCode` so a typo or a retired code fails to compile rather than
 * silently becoming an entry nothing can ever match.
 */
const MESSAGES = {
  /**
   * One message for three situations: an unknown staff code, a wrong password
   * or PIN, and a deactivated user. The API returns one code for all three on
   * purpose and spends the same key-derivation work on each so the timing does
   * not separate them either.
   *
   * Do not add a friendlier variant for any of them. "We do not recognise that
   * staff code" hands anyone with a login form a roster of who works at the
   * park, and "this account is disabled" tells a departed employee their guess
   * was right. The server declines to say which; so does this.
   *
   * It names the pair rather than either half, because the pair is exactly what
   * was refused and all that is known.
   */
  AUTH_CREDENTIALS_INVALID:
    'Those details were not accepted. Check the staff code and the password or PIN, then try again.',

  /**
   * The one refusal in this set that is deliberately distinguishable, and it
   * should be: the alternative is a staff member told their PIN is wrong when
   * it is right, for fifteen minutes, with no way to understand why.
   *
   * Worded to describe the lockout without confirming that anyone is behind the
   * staff code. "Sign-in with this staff code is blocked" is a statement about
   * what was typed; "your account is locked" is a statement that the account
   * exists, and that is a sentence the API never wrote.
   *
   * It names an owner rather than a manager because `POST
   * /v1/users/{id}/clear-lockout` sits on the identity-admin surface, which is
   * OWNER-only. Sending someone to a manager sends them to a person who will
   * find the control hidden and the route refused — fifteen minutes spent
   * asking the wrong desk.
   */
  AUTH_ACCOUNT_LOCKED:
    'Too many failed attempts. Sign-in with this staff code is blocked for 15 minutes. Wait and try again, or ask an owner to clear the lockout.',

  AUTH_SESSION_EXPIRED:
    'Your session has ended — a session lasts 12 hours, and ends after 30 minutes without activity. Sign in again to continue.',

  AUTH_NO_OUTLET_ASSIGNMENT:
    'You are not assigned to an outlet, so there is nowhere to work from. Ask a manager to assign you to one.',

  AUTH_FORBIDDEN: 'You do not have permission to do that. Ask a manager if you need it.',

  /**
   * The credential somebody else chose has not been replaced yet (FR-107), and
   * **this is the one refusal in the file the person reading it can clear on
   * their own, immediately.**
   *
   * It shared `AUTH_FORBIDDEN` until `blossom-rest-api#400` split it out on 23
   * Aug 2026, which is why the wording matters more than the average row here:
   * for two epics a provisioned user met "Ask a manager if you need it" on all
   * sixty routes that publish this — for a condition no manager can clear, on an
   * account that has every permission it needs. Being sent to fetch somebody who
   * then cannot help is how a person learns the software is broken.
   *
   * So it names the screen and asks nothing of anybody else. `/change-credential`
   * is the one route the API answers while `mustChangeCredential` is set, it is
   * declared `requireSignedIn({ allowsPendingCredential: true })`, and every role
   * can reach it — so "Change your credential" is not advice, it is the heading
   * of a page that is already open in front of them.
   *
   * The API refuses before the handler runs, so nothing was read or written, and
   * the identical request succeeds once the credential is replaced. That is why
   * this may invite the attempt again where most non-retryable copy in this file
   * must not: the thing that changes is not the request.
   */
  AUTH_CREDENTIAL_CHANGE_REQUIRED:
    'You are still signed in with the password or PIN that was set for you by somebody else, and it cannot be used for anything further until you replace it. Nothing was changed. Open Change your credential, choose your own, and then do this again.',

  /**
   * The device's lease has lapsed, and **"renew it" is the one instruction this
   * must not give** — which is exactly what the API's own published description
   * for this code says.
   *
   * `LeaseRenewalService` answers a renewal presenting a lapsed or unknown lease
   * with this same code, and says why in its own words: *"the token verified, so
   * the honest reading is a lease that no longer stands, and 'renew' is the wrong
   * instruction to give twice — the device must be registered again"*. Copy
   * lifted from the registry line would put a gate operator in a loop whose only
   * exit is somebody guessing.
   *
   * It names an **owner**, because `TerminalsController` is
   * `@RequireRole('OWNER')` on the class — a manager sent to re-register a
   * terminal meets a second refusal, and that one reads as a bug in the terminals
   * screen rather than as the wrong desk. And it says *at the machine*, because
   * registration puts the lease on the device it is run from: an owner doing this
   * from the back office on their own laptop would register the laptop.
   *
   * Deliberately silent about what is queued on the device. Ten routes raise
   * this, most of them reads, and this map has only the code to go on — a
   * promise about the outbox would be a guess on the one screen where a wrong
   * guess is expensive.
   */
  AUTH_TERMINAL_LEASE_EXPIRED:
    'This terminal’s lease has run out, so the server will not accept anything further from it. Renewing cannot recover a lease that has lapsed. An owner has to register this terminal again, at the machine itself, which issues it a fresh lease.',

  /**
   * Registration refused because the terminal has been withdrawn.
   *
   * It names the way forward, because there is one and it is not obvious: a
   * revoked device is never put back into service, so the fix is a new terminal
   * under a new prefix rather than anything that can be done to this row. Left
   * to the generic fallback this reads as a fault to report, and somebody
   * spends an afternoon waiting for support to un-revoke something that cannot
   * be un-revoked.
   */
  AUTH_TERMINAL_REVOKED:
    'This terminal has been revoked, and a revoked terminal is never put back into service. Create a new terminal with a new prefix and register the device as that one.',

  /**
   * The same idempotency key is already in flight — the first attempt has not
   * finished, rather than having failed. Retryable, and the API says so, which
   * is why this asks for a wait instead of describing a problem.
   */
  SYNC_REQUEST_IN_PROGRESS:
    'This is still being processed. Wait a moment and try again — trying again is safe and will not do it twice.',

  /**
   * The same key, a **different** payload — and the pair directly above is why
   * this needs its own sentence rather than the fallback's.
   *
   * `SYNC_REQUEST_IN_PROGRESS` is the same key with the *same* payload and is
   * retryable; a matching resubmission is answered with the first attempt's
   * outcome and never reaches here. So this one means the submission changed
   * since the attempt that claimed the key, the change was **not** recorded, and
   * the identical request will be refused identically. Both are 409s, which is
   * exactly why the status cannot be allowed to word either of them.
   *
   * `lib/idempotency-key.ts` is the other half: one key per attempt, reused by
   * every retry of *that* attempt. A corrected body is a different attempt and
   * needs a new key — so the way out is to start the form again, and the copy
   * says so rather than inviting a retry that cannot work.
   *
   * It also says to look before re-entering. The earlier submission under that
   * key may well have been recorded, and somebody who assumes otherwise records
   * it twice.
   */
  SYNC_IDEMPOTENCY_KEY_REUSED:
    'The reference this was sent under has already been used for a different submission, so this one was not recorded. Sending it again as it stands will be refused the same way. Start the form afresh so it carries a new reference — and check the list first, in case the earlier submission under that reference was recorded.',

  /**
   * A write that reached the API with no `Idempotency-Key` header at all.
   *
   * **A fault in this client and never a person's mistake**: every write this app
   * makes attaches one, so there is nothing to retype and nothing to retry. It
   * still gets a row rather than the fallback, because "report this" and "the
   * server refused this request and gave the code …" send a staff member to
   * support with different amounts of nothing.
   *
   * Not worded as a terminal problem. The API's own description was, once, and
   * that wording sent people looking for a till that was not involved — every
   * route raising this today is a back-office write.
   */
  SYNC_IDEMPOTENCY_KEY_MISSING:
    'This was sent without the reference every write has to carry, so nothing was recorded. Report this — it is a fault in the software rather than anything you did.',

  /**
   * The till's build is older than the server accepts writes from (FR-813,
   * R-809, R-810), or its `X-Write-Contract-Version` header could not be read.
   *
   * **The inverse of `DAY_CLOSE_TERMINAL_DAY_CLOSED` and the one it is likeliest
   * to be confused with.** That one is held and lands by itself; this one never
   * lands at all. The guard runs before the idempotency claim precisely so a
   * doomed write does not take a key with it, and the API is explicit that the
   * same body cannot succeed by being sent again — so the copy must close the
   * subject rather than leave a cashier watching a queue that will not drain.
   *
   * It says the write has to be settled by hand, and stops there. There is a
   * route that records an admission a till could never send
   * (`POST /v1/outlets/{outletId}/offline-ticket-records`), but no screen in this
   * app calls it yet, and naming an office screen that does not exist is how the
   * neighbouring ticket copy came to promise the opposite. Whoever settles it
   * needs the number; the number is what this asks for.
   *
   * Not worded as anybody's mistake. A till running an old build is an estate
   * that has not been updated, which is nothing the person holding it did.
   */
  SYNC_WRITE_CONTRACT_UNSUPPORTED:
    'This terminal is running software older than the server now accepts writes from, so what it sent cannot be recorded — not now, and not by sending it again. Nothing was recorded. The terminal needs updating, and what it was holding has to be settled by hand: report it with the receipt or ticket number.',

  /**
   * A hand-entered admission for a ticket the books already have (FR-823a).
   *
   * **Nothing is lost here, and the copy has to say so before anything else.**
   * Two situations reach it: the till's queued write got through in the end and
   * the ticket traded normally, or somebody has already entered it by hand with
   * different details. The same ticket with the same facts is answered `200` with
   * the record that already exists, so this is never a resubmission — it is
   * always one of those two, and in both the money is recorded.
   *
   * It says the entry that stands is the one that counts, and that correcting it
   * means a further entry rather than an edit. That route writes a ledger entry
   * and its audit entry and **deliberately no `tickets` row**, so an admission
   * recorded this way is in the books and is not an artefact any screen can void
   * — "change it" would be advice with no control behind it.
   *
   * Deliberately does not say which of the two happened. The refusal carries it
   * in `details.arrival` (`TRADED` or `RECORDED`) and this map reads `code` and
   * nothing else by design; a screen holding the response says which.
   */
  SYNC_OFFLINE_TICKET_ALREADY_RECORDED:
    'That ticket is already in the books — either it reached the office by the usual route in the end, or somebody has already entered it by hand with different details. Nothing was recorded by this attempt, and the money is not counted twice. The entry that already stands is the one that counts; a ledger entry is never edited, so anything wrong with it has to be put right by a further entry.',

  /**
   * A hand-entered admission with no written reason (FR-825).
   *
   * The reason is the whole justification for the record: it is money going into
   * the books for a ticket nobody can produce, so *why* is the only thing
   * separating it from an unexplained entry. The copy therefore states what the
   * reason is for rather than merely that a field is required.
   *
   * Names all three ways of failing it, because two of them are invisible on the
   * form: absent, blank once trimmed — a reason of spaces satisfies a `required`
   * check and says nothing — or past 500 characters.
   */
  SYNC_OFFLINE_TICKET_REASON_INVALID:
    'An admission entered by hand needs a written reason saying why the ticket never reached the office. This one had none, or none left once the spaces were taken off, or more than 500 characters. Nothing was recorded.',

  IDENTITY_OUTLET_CODE_TAKEN:
    'That outlet code is in use by another outlet. Choose a different code, or retire the outlet holding it first.',

  // Its tills' invoice series would otherwise span two GSTINs (#534).
  IDENTITY_OUTLET_TAX_IDENTITY_FIXED:
    'This outlet has tills, so its GST registration can no longer be changed.',

  // Prefixes and staff codes are never reused, including by records since
  // revoked or long departed — receipt and ticket numbers carry the prefix, and
  // a reissued one would make two terminals' history indistinguishable. Saying
  // so is the difference between "try another" and trying the same handful.
  IDENTITY_TERMINAL_PREFIX_TAKEN:
    'That terminal prefix has been issued before. Prefixes are never reused, including by terminals since revoked, so choose a different one.',

  IDENTITY_STAFF_CODE_TAKEN:
    'That staff code has been issued before. Staff codes are never reused, including for people who have left, so choose a different one.',

  /**
   * A catalogue code that has been issued before — **including to an item since
   * retired**, which is where this differs from every other "taken" in this map
   * and from `IDENTITY_OUTLET_CODE_TAKEN` directly above it.
   *
   * An outlet code comes back when its outlet is retired, so that message offers
   * retiring the holder as a way out. A catalogue code never comes back: the API
   * puts a *total* unique index on `(kind, code)` alongside the partial one,
   * because the code lands in money history that Reporting groups by, and on
   * receipts and tickets that outlive the record. So this says the code is spent
   * rather than in use, and offers no way to reclaim it, because there is none.
   *
   * It also names the kind. The uniqueness is per kind — a `TICKET_TYPE` and a
   * `PRODUCT` may both be `ADULT` — so "that code is taken" without it sends
   * somebody looking through a list the collision is not in.
   */
  CATALOGUE_CODE_TAKEN:
    'That code has already been issued for this kind of item. Catalogue codes are never reissued, including for items since retired, so choose a different one.',

  /**
   * A barcode already carried by another item on sale at one of this item's
   * outlets (blossom-rest-api#532). Unique **per outlet**, not per park: the same
   * product stocked in both coffee shops is two items under one barcode.
   *
   * Raised from four places — create, edit, restore and the outlets form — and
   * on the last two the screen never mentions a barcode, so the sentence has to
   * name it. It cannot yet say *which* item: the API raises it with no
   * `details`, and there is no barcode filter to look one up by. Name the item
   * once the API carries it, not by paging through an outlet's catalogue.
   */
  CATALOGUE_BARCODE_TAKEN: 'Another item sold at this outlet already uses that barcode.',

  /**
   * Stock tracking asked for on something that is not a product (FR-212).
   *
   * The forms disable the switch rather than letting it be sent, so reaching
   * this means the two disagreed — a stale tab, or a write replayed from a
   * queue. It still needs a sentence, and the sentence is the rule rather than
   * the refusal.
   */
  CATALOGUE_STOCK_ON_NON_PRODUCT:
    'Only a product can count stock. Ticket types and activities are never counted, so stock tracking cannot be turned on for one.',

  /**
   * A stock-tracked item named an outlet that carries no stock (FR-208).
   *
   * Deliberately unnamed here, and a screen that knows which outlet was meant
   * says so itself: the refusal carries no `details`, and this map reads `code`
   * and nothing else by design. What it can do is state the rule and say plainly
   * that nothing was written, which is the question somebody asks first.
   */
  CATALOGUE_STOCK_NOT_AVAILABLE_HERE:
    'An item that counts stock can only be sold where stock is counted, and one of the outlets chosen does not carry any. Nothing was changed.',

  /**
   * A write aimed at an item that has been retired, or a restore aimed at one
   * that has not.
   *
   * **One code, two situations, and they get one message on purpose.** The API
   * defines it as "a write against a retired item that is not a restore", so an
   * edit, a reprice and a second retirement all land here; a restore of a live
   * item is refused as `RESOURCE_NOT_FOUND` instead, which already has its own
   * copy. What every one of them has in common is that the screen is looking at
   * a stale copy of the row, so the sentence is about that rather than about
   * which button was pressed.
   *
   * No bare "try again": this is non-retryable, and the identical request fails
   * identically. Reopening the item is the corrected attempt.
   */
  CATALOGUE_ITEM_RETIRED:
    'That item has been retired, so it cannot be changed. Nothing was written. Turn on Show retired items to see it as it stands, and restore it first if it should be on sale.',

  /**
   * A price the API will not carry — negative, or beyond what an amount can
   * hold. Zero is not one of these: a free child ticket is a real price.
   */
  CATALOGUE_PRICE_INVALID:
    'That amount cannot be used as a price. It must be zero or more, and within the range the system carries.',

  /**
   * A till presenting a catalogue version this outlet never issued (FR-227).
   *
   * **Nothing raises this today**, and that is recorded here rather than left
   * for the next reader to rediscover: the code is in the registry and in
   * `error-code.ts`, the catalogue contract publishes it, and no handler throws
   * it and no route declares it in the served document. It gets a row on
   * `BUSINESS_DAY_CLOSED`'s precedent — the gap it would fall through is silent,
   * and one sentence now costs less than the first person to meet it being told
   * to report a refusal that is the system working.
   *
   * Worded as the terminal being behind rather than as a bad request, because
   * that is what it will mean when it does fire: a device selling from a
   * catalogue it took a copy of before the outlet's was reissued. The way out is
   * the terminal fetching the current one, which it does by itself on
   * registration, re-registration and renewal — so this asks for nothing to be
   * retyped.
   */
  CATALOGUE_VERSION_UNKNOWN:
    'This terminal is selling from a version of the catalogue this outlet never issued, so the write was refused. Nothing was recorded. The terminal has to take a fresh copy of the outlet’s catalogue before it can sell from it.',

  IDENTITY_LAST_OWNER:
    "This is the park's last active owner, and the park cannot be left without one. Give someone else the owner role first, then change this one.",

  IDENTITY_ROLE_ESCALATION:
    'You cannot grant a role you do not hold yourself, and you cannot change your own role. Ask an owner to make this change.',

  /**
   * A new person given no credential for the place they actually work (#173).
   *
   * **The rule is "the credential their role signs in with", not "a password
   * always"**, and stating it that way is the point: a gate hand has no business
   * with a back-office password, and copy that asked for one would have somebody
   * setting a password for a person who will only ever meet a PIN pad. So this
   * names both halves of the mapping rather than the field that was empty —
   * owners and managers work in the back office and take a password, outlet and
   * gate staff work at a till and take a PIN.
   *
   * It is not "exactly one". An owner who also works a gate may hold both, and
   * the API refuses neither, so the copy says what is *needed* rather than what
   * is allowed.
   *
   * Raised only by `POST /v1/users`. `PATCH /v1/users/{id}` deliberately does not
   * enforce it — the server cannot tell a promotion that needs a password from a
   * rename that must not reset a good one — so this reads on the create form and
   * says nothing was created, which on that form is the question.
   */
  IDENTITY_CREDENTIAL_REQUIRED:
    'This person has no credential for the place they work, so they could not be created. An owner or a manager works in the back office and needs a password; outlet and gate staff work at a till and need a PIN. Nothing was created — set the one their role signs in with and record them again.',

  /**
   * A void aimed at a ticket whose **own** business day has since closed.
   *
   * Not "already voided", and the two must never be worded alike — that is the
   * whole of FR-332 on this side. Already voided is `200` and is success; this
   * is a refusal, nothing was written, and the correction now belongs to Day
   * Close. A screen that blurred them would tell somebody a ticket was cancelled
   * when it stands, on a row carrying money.
   *
   * **The cut-off is the ticket's day, not today's**, which is why this does not
   * say "the day has closed" — an outlet with a day open right now still refuses
   * a ticket from a day it has closed since, and somebody reading "the day is
   * closed" while selling tickets would report it as a bug rather than as the
   * rule it is.
   *
   * It names the way out because there is one and it is not obvious: reopening a
   * closed day is an **owner's** act, and nothing on the screen that raised this
   * can do it. Left to the generic fallback this reads as a fault to report.
   *
   * An owner and not a manager, corrected in #125. `POST
   * /v1/outlets/{outletId}/business-days/{businessDate}/reopen` is owner-only —
   * "a manager may not reopen, whatever outlet they are assigned to, because
   * reopening undoes another person's declaration" (CL-603). This said "a
   * manager" for two epics, which sent somebody to fetch a person the API then
   * refuses; the second refusal reads as a bug in the reopen screen rather than
   * as the wrong person having been asked.
   */
  TICKETING_DAY_CLOSED:
    'That ticket belongs to a business day that has been closed, so it can no longer be voided here — even if this outlet has a day open now. Nothing was changed. An owner must reopen that day to correct it.',

  /**
   * The void id sent is already recorded against a **different** ticket.
   *
   * A client bug and never a person's: ids for voids are minted by whatever made
   * the request, one per void, and reaching this means one was reused. There is
   * nothing to retype and nothing to retry — the identical request fails
   * identically — so this asks for it to be reported rather than offering a way
   * forward that does not exist.
   *
   * Deliberately says nothing about the other ticket. The API withholds it on
   * purpose (the refusal names the field and not the ticket), because that id
   * may belong to an outlet the caller cannot reach.
   */
  TICKETING_VOID_IMMUTABLE:
    'This void could not be recorded: its reference is already in use by another void. Nothing was changed, and trying again will not help. Report this — it is a fault in the software rather than anything you did.',

  /**
   * A ticket for something this outlet does not offer.
   *
   * The gate's counterpart to `SALES_ITEM_NOT_SELLABLE_HERE`, and read the same
   * way that one is: after the fact, off a queued write, with the visitor long
   * since through the gate and the money in the drawer. Non-retryable, so it
   * says what was refused rather than offering a retry that cannot work.
   *
   * **The admission happened and the copy says so.** That is the fact somebody
   * has to act on — the takings are short by a ticket that was genuinely issued —
   * and it is the half the generic fallback hides completely.
   *
   * Names an owner for the setting, because what an outlet sells is set on the
   * outlet and `OutletsController` is owner-only; a manager sent to change it
   * meets a refusal.
   */
  TICKETING_ITEM_NOT_SELLABLE_HERE:
    'That ticket was for something this outlet is not set up to issue, so it was not recorded. The visitor was admitted and the money was taken, so the takings are short by it. An owner sets what an outlet offers in the back office. Report this with the ticket number so the admission can be put into the books.',

  /**
   * A shop product issued as an admission.
   *
   * The gate's counterpart to `SALES_ITEM_KIND_INVALID`, and a **fault** rather
   * than a mis-tap for the same reason that one is: the gate screen draws only
   * ticket types and activities, so a product reaching this route means the
   * screen and the write disagree. Nobody at the gate did anything they could
   * have done differently, and the copy must not imply otherwise.
   *
   * The API's own wording is about kinds; a person needs it about counters.
   */
  TICKETING_ITEM_KIND_INVALID:
    'That admission was issued for a shop product, which is sold on a receipt at a counter rather than printed as a ticket, so it was not recorded. Report this with the ticket number — it is a fault in the software rather than anything anybody did.',

  /**
   * The arithmetic disagreed on a ticket.
   *
   * **Always a client fault**, exactly as `SALES_PRICE_MISMATCH` is: the till
   * takes the amount from the price row it names and the server recomputes it
   * from the same row, so the two disagreeing means the till sent an amount that
   * is not its own item's price. It also covers a price row that belongs to a
   * different item, which is the same disagreement one step earlier.
   *
   * Read after the fact off a queued write, so it says the admission happened.
   */
  TICKETING_PRICE_MISMATCH:
    'That ticket did not add up against the price it names, so it was not recorded. The visitor was admitted and the money was taken. Report this with the ticket number — it is a fault in the software rather than anything anybody did.',

  /**
   * A cash ticket that carried a payment reference — a *rule* rather than a
   * fault, and worded as one. Screens are built so it is unreachable: the gate
   * does not offer the field on cash, clears it on a switch, and the terminal
   * refuses the pair before a number is spent. Seeing it means one of those was
   * not.
   *
   * **It reads after the fact, never at the gate.** A ticket is queued on the
   * device and drained later, so by the time this comes back the visitor is
   * inside the garden with the paper in their hand and the money is in the
   * drawer. It is non-retryable, so it says what was refused rather than
   * offering a retry that cannot work.
   *
   * **And it deliberately does not say "re-enter it", the way
   * `SALES_PAYMENT_REFERENCE_NOT_ALLOWED` does.** Issuing is terminal-only —
   * the session-authenticated ticket routes are three reads and a void — so
   * there is no route anywhere that can record an admission the server refused.
   * Promising a correction that has no mechanism is worse than naming the gap.
   */
  TICKETING_PAYMENT_REFERENCE_NOT_ALLOWED:
    'That ticket was recorded as cash but carried a payment reference, which only a UPI or card ticket may have, so it was not recorded. The visitor was admitted and the money was taken, and there is currently no way to enter this ticket in the office. Report it with the ticket number.',

  /**
   * A sale the terminal recorded and the server refused.
   *
   * **All six of these are read after the fact, not at the till.** A sale is
   * queued on the device and drained later, so by the time one of these comes
   * back the customer has walked away with a receipt and the money is in the
   * drawer. None of them is retryable — each means the submission can never
   * succeed as sent — so every message says what was refused and who settles it,
   * and none offers a retry that cannot work.
   *
   * They are worded for the refused-writes screen, which is where a queued write
   * surfaces its refusal, rather than for a cashier mid-sale.
   */
  SALES_EMPTY_BASKET:
    'That sale reached the office with nothing in it, so it was not recorded. The money it took, if any, is not in the day’s figures. Report it with the receipt number.',

  /**
   * The API's own wording is about kinds; a person needs it about counters.
   * Since blossom-rest-api#527 admission is a sale line on a **taxed** price,
   * so this is a till that sent admission on an untaxed price as a sale rather
   * than a ticket — a fault rather than a mis-tap, because the gate routes on
   * the price row it holds (#274).
   */
  SALES_ITEM_KIND_INVALID:
    'That sale included admission with no GST on its price, which is issued as a ticket rather than sold on a tax invoice, so it was not recorded. Report this — it is a fault in the software rather than anything anybody did.',

  SALES_ITEM_NOT_SELLABLE_HERE:
    'That sale included something this outlet is not set up to sell, so it was not recorded. An owner sets what an outlet sells in the back office. The sale needs re-entering there once it is.',

  /**
   * A **different** basket already recorded under that sale's id.
   *
   * Worded on `TICKETING_VOID_IMMUTABLE`'s pattern, because it is the same
   * failure a layer up: ids for sales are minted by the till, one per sale, and
   * the same id with the same facts is answered `200` with the original outcome.
   * So reaching this means one id is claimed by two different baskets — a client
   * fault, never a cashier's, with nothing to retype and nothing to retry.
   *
   * **Not "already recorded", which is what the code's name suggests and would
   * be the expensive misreading.** "This sale is already recorded" tells somebody
   * the money is in the books; what actually happened is that *a* sale is
   * recorded under that reference and it is not this one, so this basket's money
   * is in the drawer and in nobody's figures. The distinction is the same one
   * `SALES_ALREADY_REFUNDED` and `SALES_NUMBER_CONFLICT` each turn on.
   */
  SALES_SALE_IMMUTABLE:
    'This sale could not be recorded: a different sale is already recorded under its reference. Nothing was changed by this attempt and sending it again will not help, so the money it took is not in the day’s figures. Report it with the receipt number — it is a fault in the software rather than anything anybody did.',

  /**
   * The arithmetic disagreed. **Always a client fault**: the till computes each
   * line from the price row it names, and the server recomputes it from the same
   * row — so the two disagreeing means the till sent an amount that is not its
   * own quantity times its own price.
   */
  SALES_PRICE_MISMATCH:
    'A line on that sale did not add up against the price it names, so the sale was not recorded. Report this with the receipt number — it is a fault in the software rather than anything anybody did.',

  SALES_TOTAL_MISMATCH:
    'The total on that sale did not match the lines under it, so the sale was not recorded. Report this with the receipt number — it is a fault in the software rather than anything anybody did.',

  /**
   * A tax invoice number where none belonged, or none where one did
   * (blossom-rest-api#524). **A client fault, like the two above**: whether a
   * basket is taxed is read off the price rows it names, which never change,
   * so the till and the server can only disagree if the till's own arithmetic
   * is wrong — and a replay fails identically. Worded for the refused-writes
   * screen, where a queued sale surfaces it.
   */
  SALES_INVOICE_NUMBER_MISMATCH:
    'That sale’s tax invoice number did not match what was sold — a sale with a taxed item needs one, and a sale without must not carry one — so it was not recorded. Report this with the receipt number — it is a fault in the software rather than anything anybody did.',

  /**
   * The outlet's GST registration lacks something every tax invoice prints, or
   * the outlet names none (blossom-rest-api#524, #534). It changes only when an
   * owner sets it, so the copy names the screens that do — by the labels the
   * back office's own navigation gives them — rather than offering a retry.
   * `details` names the outlet and the unset fields, and this map reads `code`
   * alone, so the sentence names all of them.
   */
  SALES_TAX_INVOICE_UNCONFIGURED:
    'No tax invoice can be printed yet: this outlet has no GST registration, or its registered name, address, GSTIN and invoice title have not all been set. An owner sets them in the back office, under Setup → Outlets and Setup → Tax identities.',

  /**
   * The one in this set that is a *rule* rather than a fault, and it is worded
   * as one: a cash sale may not carry a reference at all. Screens are built so
   * this is unreachable — the field is not offered on cash — so seeing it means
   * one was not.
   */
  SALES_PAYMENT_REFERENCE_NOT_ALLOWED:
    'That sale was recorded as cash but carried a payment reference, which only a UPI or card sale may have, so it was not recorded. Check how it was actually paid and re-enter it.',

  /**
   * A **different** sale already holds that receipt number on that terminal.
   *
   * A real conflict rather than a duplicate, and it needs a person: the same
   * sale resubmitted is answered `200` with the original outcome, so reaching
   * this means two different baskets both claim one number — and one of them is
   * on paper in somebody's hand. Retrying is precisely what must not happen.
   */
  SALES_NUMBER_CONFLICT:
    'Another sale is already recorded under that receipt number at this till, so this one was not. Two different sales cannot share a number. Report it with both receipt numbers — it needs settling by hand and must not be sent again.',

  /**
   * A **second** refund aimed at a sale one already reverses (FR-446).
   *
   * **Not "you already did that", and the distinction is the whole of FR-445 on
   * this side.** Resending the *same* refund — the same client-minted refund id —
   * is answered `200` with the original outcome and never reaches here; a screen
   * or a queue seeing this code sent a *different* refund id against a sale that
   * is already reversed. A sale is refunded at most once, so there is nothing to
   * retry and nothing to correct: the money went back the first time.
   *
   * It says the money went back rather than merely refusing, because the
   * question somebody actually has — at a counter, with a customer — is whether
   * the customer has been paid. They have, once, and the sale's own record says
   * when.
   *
   * Deliberately says nothing about who refunded it or when. `SaleResource`
   * carries `refundedAt` and the refusal carries no `details`, so this map, which
   * reads `code` and nothing else, could not honestly name either.
   */
  SALES_ALREADY_REFUNDED:
    'That sale has already been refunded, and a sale can only be refunded once. Nothing was changed by this attempt and no money moved a second time — the refund that stands is the first one. Open the sale to see when it was reversed.',

  /**
   * A refund aimed at a sale whose **own** business day has since closed
   * (FR-443).
   *
   * Worded on `TICKETING_DAY_CLOSED`'s pattern deliberately, because it is the
   * same rule one epic later and a person who has met one should recognise the
   * other. The two halves that matter:
   *
   * - **The cut-off is the sale's day, not today's.** An outlet with a day open
   *   right now still refuses a sale from a day it has closed since, so this does
   *   not say "the day is closed" — somebody reading that while ringing up sales
   *   would report it as a bug rather than as the rule it is.
   * - **It is an answer, not a fault and not a retry.** Nothing was written, the
   *   identical request fails identically, and the correction belongs to Day
   *   Close. So it names the way out — reopening that day is an **owner's** act
   *   and nothing on the screen that raised this can do it — rather than leaving
   *   the generic fallback to read as something to report. An owner and not a
   *   manager, corrected in #125 for the reason given on `TICKETING_DAY_CLOSED`
   *   above: the reopen route is owner-only (CL-603).
   */
  /**
   * A reprint of a sale with an admission line (blossom-rest-api#527): its tax
   * invoice is the visitor's entry ticket, so a second copy would be a free
   * entry (CL-001), and the same sale is refused every time. Goods-only sales
   * reprint as before. Worded for the back office, where the record is already
   * on screen; a till substitutes its own sentence (`till-messages.ts`).
   */
  SALES_REPRINT_NOT_ALLOWED:
    'This sale includes admission, and its tax invoice is the visitor’s entry ticket, so it cannot be printed again — a second copy would be a free entry. Nothing was printed and the sale is unchanged; its record stays here to read.',

  SALES_DAY_CLOSED:
    'That sale belongs to a business day that has been closed, so it can no longer be refunded here — even if this outlet has a day open now. Nothing was changed and no money moved. An owner must reopen that day to refund it.',

  /**
   * The closed-day refusal a **terminal** gets, and the inverse of the two above
   * in the only way that matters to the person reading it.
   *
   * `TICKETING_DAY_CLOSED` and `SALES_DAY_CLOSED` are the back-office answers to
   * this same condition, and both end by telling somebody what to do, because
   * for those the identical request genuinely fails identically: a person at a
   * keyboard has no queue behind them, so the write is gone and the correction
   * is an act somebody performs. This one is lease-authenticated, and the API
   * marks it **retryable** — the only client-facing refusal in the whole
   * registry that is (409, `retryable: yes`, FR-656, CL-604). The condition is a
   * state somebody else created, the refusal releases the idempotency key, and
   * our outbox replays the stored body verbatim, so the identical submission is
   * accepted the moment an owner reopens that day.
   *
   * So the copy carries the opposite of an instruction. There is nothing for the
   * cashier to do, nothing to re-enter, and nobody to fetch: the sync engine
   * classifies this as retryable, takes the `scheduleRetry` path with unbounded
   * attempts, and the submission lands by itself. **Worded like its two
   * neighbours it would have a cashier re-ring a sale that is already safely
   * queued, and the customer pays twice** — which is why this says plainly that
   * entering it a second time would record it twice, rather than leaving that
   * to be inferred.
   *
   * It says "held on this terminal" rather than naming a sale, a ticket or a
   * void: four lease-authenticated routes raise it — ticket sale and void, sale
   * and refund — and this map reads `code` and nothing else, so it cannot
   * honestly name which.
   *
   * The way out names an **owner**, not a manager, and so do the two messages
   * above as of #125. The reopen route is owner-only: "a manager may not reopen,
   * whatever outlet they are assigned to, because reopening undoes another
   * person's declaration" (CL-603). All three closed-day answers now send people
   * to the one desk that can actually clear the condition.
   */
  DAY_CLOSE_TERMINAL_DAY_CLOSED:
    "That outlet's business day has been closed, so nothing further can be recorded against it. What you sent is held on this terminal and will send by itself once an owner reopens that day. There is nothing to redo here — entering it a second time would record it twice.",

  /**
   * A back-office write into a day that has been closed — the day the entry
   * would **land in**, which is a different question from the day the record
   * being corrected belongs to.
   *
   * That distinction is the whole reason this needs its own words rather than
   * `TICKETING_DAY_CLOSED`'s: a ticket from a still-open earlier day, voided on
   * a today that has been shut, gets past that cut-off and is stopped by this
   * one (FR-612, FR-621). Somebody reading "that ticket belongs to a closed day"
   * about a ticket whose day is plainly open would report it as a bug.
   *
   * Non-retryable, and the copy must promise no later delivery. It reaches a
   * person at a keyboard with no queue behind them, so nothing is holding this
   * and saying otherwise would be `DAY_CLOSE_TERMINAL_DAY_CLOSED`'s inversion
   * with the sign flipped — the more expensive of the two mistakes, because the
   * write really is gone.
   *
   * Written before anything could raise it. Machine imports now do, per bill
   * inside a `201` (the file's other bills went in) and on a machine-bill
   * reversal, which lands on the bill's own day — so "this" is the one bill or
   * reversal, and once the day is reopened the same upload or reversal goes in.
   */
  BUSINESS_DAY_CLOSED:
    "The business day this would be recorded in has been closed, and a closed day's figures cannot move once it has been signed off. Nothing was recorded. An owner can reopen that day if this still has to go in, and then it can be submitted again.",

  /**
   * A day asked to close that is closed already.
   *
   * **Not a fault and not a race to be reported.** Two managers cashing up
   * together, or one who closed and came back to the tab, is the ordinary way to
   * reach it — so this states the position and names what to do if the figures
   * are actually wrong, rather than treating a second close as an error.
   *
   * Reopening is an owner's act (CL-603), named for the reason the closed-day
   * family all name it: a manager sent to reopen meets a second refusal.
   */
  DAY_CLOSE_ALREADY_CLOSED:
    'That business day is already closed, so it cannot be closed again. Nothing was changed, and the figures that were signed off still stand. If they need correcting, an owner has to reopen the day first.',

  /**
   * A reopen aimed at a day that is not closed — the mirror of the row above,
   * and reached the same ordinary way.
   *
   * Says the day is open rather than that the request was wrong, because on the
   * likeliest path somebody else has already reopened it and the screen is
   * simply behind. Refreshing is the way out, and it costs nothing to try.
   */
  DAY_CLOSE_NOT_CLOSED:
    'That business day is not closed, so there is nothing to reopen. Nothing was changed — it may already have been reopened since this screen last read it, so refresh the list.',

  /**
   * The counted cash figure the close was declared with.
   *
   * **Zero is valid and the copy has to say so**, which is the trap here: an
   * outlet that took nothing all day closes on a counted zero, and copy that
   * said "must be more than zero" would be wrong on exactly the day somebody
   * most needs to be believed. What is refused is a negative figure, a fraction
   * of a paisa, or one past the bound.
   *
   * The bound is stated for `STOCK_QUANTITY_INVALID`'s reason: it is a policy
   * ceiling set well below what the column could hold, so nobody can infer it,
   * and without it a person retypes a number that can never be accepted.
   */
  DAY_CLOSE_COUNT_INVALID:
    'That counted cash figure cannot be used. It has to be a whole amount, never negative, and no more than ₹1,00,00,000. Zero is allowed and means an empty drawer. Nothing was recorded.',

  /**
   * A reopen with no written reason (FR-626).
   *
   * Reopening undoes a declaration somebody else signed off, so the reason is
   * the trail rather than a formality — and the copy says which, because "reason
   * is required" invites the shortest thing that clears the field.
   *
   * Names all three ways of failing it: absent, blank once trimmed (a reason of
   * spaces passes a `required` check and says nothing), or past 500 characters.
   */
  DAY_CLOSE_REASON_INVALID:
    'Reopening a day needs a written reason — it undoes a close somebody else signed off, and the reason is what explains that afterwards. This one had none, or none left once the spaces were taken off, or more than 500 characters. Nothing was changed.',

  /**
   * A payout amount that cannot be recorded (FR-623).
   *
   * **A payout is a positive magnitude and the direction is in the act**, so
   * zero and negatives are refused. That is worth stating rather than leaving as
   * "invalid": somebody who thinks cash leaving is negative will type a minus
   * sign, and the refusal on its own does not tell them the system disagrees.
   */
  DAY_CLOSE_PAYOUT_AMOUNT_INVALID:
    'That payout amount cannot be used. It has to be a whole amount of at least 1 paisa and no more than ₹1,00,00,000, and always positive — cash leaving the drawer is what a payout already means, so there is no minus sign to add. Nothing was recorded.',

  /**
   * A payout with no written reason (FR-622).
   *
   * The API's own note is the sentence worth keeping: *"a payout with no reason
   * is untraceable cash"*. The copy says that rather than naming a required
   * field, because it is the difference between somebody writing "cash" and
   * writing what the money was for.
   */
  DAY_CLOSE_PAYOUT_REASON_INVALID:
    'A payout needs a written reason — cash out of the drawer with nothing said about it cannot be traced afterwards. This one had none, or none left once the spaces were taken off, or more than 500 characters. Nothing was recorded.',

  /**
   * An amount the ledger will not carry.
   *
   * **Triaged cold and moved, and the move is a finding rather than a
   * preference.** `LEDGER_ENTRY_IMMUTABLE` beside it is genuinely unreachable —
   * no route in the served document publishes it — but this one is published by
   * four, and one of them is `POST /v1/outlets/{outletId}/offline-ticket-records`:
   * a back-office form a manager types an amount into by hand. It is the same
   * form whose other two refusals are written above, so leaving this to the
   * fallback would have had one field of three answer "report this".
   *
   * Zero is valid — the ledger carries a free admission — so the floor stated is
   * zero and not one, unlike the payout and expense rows above.
   */
  LEDGER_AMOUNT_INVALID:
    'That amount cannot be recorded. It has to be a whole amount of paise, never negative, and within the range the system carries. Zero is allowed. Nothing was recorded.',

  /**
   * The item is real, the request is well formed, and the item simply does not
   * count stock — so there is no level for a delivery, a correction or a count to
   * move (FR-529). Not a typing mistake, which is why the copy does not offer to
   * check the spelling: the item was found.
   *
   * It names an owner. Stock tracking is a flag on the catalogue item, and
   * `CatalogueItemsController` is `@RequireRole('OWNER')` while the three stock
   * write routes are `OWNER, MANAGER` — so the manager who most often meets this
   * cannot clear it, and sending them to a manager sends them to their own desk.
   *
   * **Says "one of the items" rather than naming it.** The refusal does carry the
   * ids in `details` (`{ outletId, catalogueItemIds }`), and this map reads `code`
   * and nothing else by design — as `CATALOGUE_STOCK_NOT_AVAILABLE_HERE` above
   * already records. A screen holding the lines knows which one it sent and says
   * so itself; ids are not what a person reads anyway.
   */
  STOCK_ITEM_NOT_TRACKED:
    'One of the items named does not count stock, so there is no level for this to move. Nothing was recorded. Take that line off, or ask an owner to turn stock tracking on for the item first.',

  /**
   * A place-level fact rather than a per-item one, and the copy leads with that:
   * somebody who reads this as being about the line they were editing will go on
   * editing lines.
   *
   * The mirror of `STOCK_ITEM_NOT_TRACKED` on the other flag, and separate from
   * it because a different person fixes it in a different place — this one is the
   * outlet's, and outlets are `@RequireRole('OWNER')` too.
   *
   * Note for anyone tempted to reuse this wording on a till: a **sale** at such an
   * outlet is not refused at all. It records no movement and succeeds. Only the
   * deliberate back-office acts refuse here.
   */
  STOCK_OUTLET_CARRIES_NO_STOCK:
    'This outlet does not carry stock at all, so nothing can be delivered to it, corrected at it or counted there. That is a fact about the place rather than about anything on this form. Nothing was recorded. Choose an outlet that holds goods, or ask an owner to set this one up to carry stock.',

  /**
   * One code for four ways of getting a quantity wrong — zero, negative,
   * fractional, or past the bound — because they ask the person the same thing.
   *
   * **The floor is not the same on all three forms**, which is the trap in
   * wording this: a delivery line and a correction line are at least 1, but a
   * stocktake count of 0 is valid and means an empty shelf. Copy that said "must
   * be at least one" would be wrong on the form where the mistake is likeliest.
   * So the sentence states the shared rule and then names each floor.
   *
   * The bound is 1,000,000 on all three, and stating it is the point of a
   * separate row: it is a policy bound set well below what the column could hold,
   * so a person cannot infer it from anywhere and the generic fallback leaves
   * them retyping a number that can never be accepted.
   */
  STOCK_QUANTITY_INVALID:
    'A quantity here cannot be used. It must be a whole number of items — never a fraction, never negative — and no more than 1,000,000. A delivery or a correction moves at least one; a count may be zero, which means an empty shelf. Nothing was recorded.',

  /**
   * A correction reason outside the published set.
   *
   * **Should be unreachable.** The reason comes from a picker drawing the
   * published list, so a person seeing this means the picker offered something
   * the server does not accept — a stale tab, or a list that has drifted. The
   * copy therefore does both jobs: tells them to pick from the list, and tells
   * them to report it if the list is where it came from.
   */
  STOCK_REASON_INVALID:
    'That is not one of the correction reasons the system publishes, so nothing was recorded. Choose a reason from the list. If the list is where it came from, report this — the screen and the server disagree about what the reasons are.',

  /**
   * The same item on two lines — **the likeliest refusal in this epic**, because
   * it is reached by an ordinary manager picking an item twice on a form with
   * many lines.
   *
   * Says to merge rather than to delete one of them: on a delivery the two lines
   * are usually both real and their quantities belong together, and the
   * alternative reading loses goods. It stops short of saying to add the
   * quantities up, because on a stocktake that would be wrong — two counts of one
   * shelf are one count, not their sum.
   *
   * Not named here, and unlike `STOCK_ITEM_NOT_TRACKED` the refusal genuinely
   * could not name it: this one is raised in the validation pipe, and its
   * `details.fields` gives the array property that broke the rule rather than the
   * item that broke it.
   */
  STOCK_DUPLICATE_ITEM_IN_REQUEST:
    'The same item is on more than one line, and one submission may name each item only once. Nothing was recorded. Merge those lines into one, then record it again.',

  /**
   * A recorded count cannot be amended, and this is a fact about the world rather
   * than a policy a screen could soften: `stocktakes` is append-only by database
   * trigger, and there is no route that edits one.
   *
   * Says why, briefly, because "cannot be amended" alone reads as an arbitrary
   * refusal from a system that plainly stores the number. The reason it cannot is
   * that the variance was derived from that number and a compensating movement
   * was already written against it — editing the count leaves a level that no
   * longer follows from its own log.
   *
   * Named for the immutable codes in Ticketing and Sales but **not their shape**:
   * those mean "resubmitted with different facts", which implies a matching
   * resubmission succeeds. This one has no such half. A stocktake reaches it by
   * being amended at all, so the way forward is a new count and never a retry.
   */
  STOCK_STOCKTAKE_IMMUTABLE:
    'That count is already recorded, and a recorded count is never amended — the variance and the movement that settled it were worked out from it at the time. Nothing was changed. To correct it, record a new count; both stay in the log.',

  /**
   * An expense amount that cannot be recorded (FR-701).
   *
   * **The correction rule is the part worth saying** (R-707): an expense is
   * always positive, and one recorded too large is put right by a *further*
   * expense naming it, never by a negative amount. Somebody who reaches this by
   * typing a minus sign is trying to correct something, and the refusal alone
   * would leave them looking for a form that does not exist.
   */
  EXPENSE_AMOUNT_INVALID:
    'That expense amount cannot be used. It has to be a whole amount of at least 1 paisa and no more than ₹1,00,00,000, and always positive. To put right an expense that was recorded too large, record a further one against it rather than a negative amount. Nothing was recorded.',

  /**
   * An expense with no written reason (FR-704).
   *
   * **The record cannot be edited afterwards**, and saying so here is the point:
   * this is the last moment the reason can be got right at no cost, and a person
   * who knows that writes a different sentence from one who thinks they can come
   * back to it. An unreasoned expense is exactly the mystery variance the epic
   * exists to end, and it can only ever be compensated, never corrected.
   */
  EXPENSE_REASON_INVALID:
    'An expense needs a written reason saying what the money was for. This one had none, or none left once the spaces were taken off, or more than 500 characters. Nothing was recorded — and an expense cannot be edited once it is in, so it is worth saying properly now.',

  /**
   * A vendor name already used by an active vendor, ignoring case. A retired
   * vendor's name is free, so the clash is always with one still on the list.
   */
  EXPENSE_VENDOR_NAME_TAKEN:
    'A vendor with that name is already on the list. Nothing was saved — use the one that is there, or tell the two apart in the name.',

  /**
   * An expense or a revenue-share agreement naming a vendor that is retired or
   * does not exist. One code for both, deliberately (blossom-rest-api#512): the
   * remedy is the same.
   *
   * **Two forms read this**, and one sentence has to be right on both: an
   * expense names who was paid, and an agreement names the partner, who is a
   * vendor (#258). The no-vendor way out exists only on the expense, so it is
   * said as belonging there.
   */
  EXPENSE_VENDOR_UNAVAILABLE:
    'That vendor has been retired, so nothing new can name it. Nothing was recorded. Choose another vendor — on an expense, you can also record it without one.',

  /**
   * The bytes are not a JPEG, PNG, WebP or PDF, whatever the name or the picker
   * said (#513). The API sniffs the leading bytes, so renaming will not help.
   */
  EXPENSE_ATTACHMENT_TYPE_UNSUPPORTED:
    'Attach a photo (JPEG, PNG, WebP) or a PDF. That file is none of these, so nothing was attached.',

  /**
   * The sixth attachment on an expense. **Attachments are append-only**, so the
   * sentence a person needs is that none can be removed to make room.
   */
  EXPENSE_ATTACHMENT_LIMIT_REACHED:
    'An expense can have at most 5 attachments, and this one has 5 already. Nothing was attached — attachments cannot be removed to make room.',

  /**
   * A reporting period that cannot be read at all — it ends before it starts, or
   * one of the two bounds is not a real calendar date.
   *
   * One code for both, and they get one sentence naming each, because a person
   * looking at two date fields cannot tell from the refusal which of them the
   * server objected to and the answer is "look at both".
   */
  REPORTING_PERIOD_INVALID:
    'Those dates do not make a period that can be reported on: it ends before it starts, or one of them is not a real date. Nothing was changed — choose the two dates again.',

  /**
   * A period longer than the 366 days a single report covers.
   *
   * States the number, because it is a policy bound nobody can infer, and offers
   * running the report in parts — which is the actual way to get the answer for a
   * longer span and is not obvious from a refusal.
   */
  REPORTING_PERIOD_TOO_LONG:
    'That period is longer than the 366 days one report covers. Choose a shorter period, or run it in parts and add the figures up.',

  /**
   * A period ending after the current business day.
   *
   * **The cut-off is the park's business date, not the machine's clock**, and it
   * rolls at midnight in Kolkata (FR-726). That matters for the person who meets
   * this: an owner asking for today's takings shortly after midnight is asking
   * about a day that has already turned, and copy saying "in the future" about
   * what they think of as tonight would read as a bug. So this says "the day the
   * park is currently on" rather than "today".
   */
  REPORTING_PERIOD_IN_FUTURE:
    'That period ends after the day the park is currently on, and there are no figures for a day that has not happened yet. Choose an end date no later than the current business day.',

  // ── Daily sheet (blossom-rest-api #516, #521) ───────────────────────────────
  //
  // Every one is non-retryable. The three corrections below are records that are
  // never edited: a second figure is a new row naming the current one, and the
  // refusal carries that one's id in `details` so a screen can correct it
  // without the person typing anything again.

  /**
   * A plain declaration for an outlet-day that already has one (#516).
   * `details.currentDeclarationId` names it; the UPI screen offers the
   * correction from there, so this says what that is rather than how to find it.
   */
  REPORTING_UPI_ALREADY_DECLARED:
    'That outlet already has UPI declared for that day, and a declaration is never overwritten. Nothing was recorded. Correct the figure that is there instead — the correction replaces it, and both are kept.',

  /**
   * Somebody corrected the same figure first (#516). Only the current one may
   * be corrected, so two people correcting it cannot both win. "Reload" is the
   * part that matters: the figure on screen is no longer the one that stands.
   */
  REPORTING_UPI_DECLARATION_SUPERSEDED:
    'Somebody corrected that UPI figure while you were working on it, so yours was not recorded. Reload to see the figure that stands now, then correct that one if it is still wrong.',

  /**
   * A period sharing a day with the partner's other agreement at that outlet
   * (#516). `details.agreementId` names it. A rate change is never an edit: the
   * current agreement is closed and the next opened from the day after.
   */
  REPORTING_REVENUE_SHARE_OVERLAP:
    'That partner already has an agreement at this outlet covering some of those days. Nothing was recorded. Close the current agreement first — set the last day it applies — then open the new one from the day after.',

  /**
   * An agreement's last day is set once and never moved (#516), so a second
   * close is refused. A later change of rate is a new agreement.
   */
  REPORTING_REVENUE_SHARE_ALREADY_ENDED:
    'That agreement already has a last day, and it is set once and never moved. Nothing was changed. To change the rate from a later day, open a new agreement.',

  /**
   * A first opening balance when the park has one — one anchor for the whole
   * park, not one per outlet (#529). `details.currentOpeningBalanceId` names
   * it; the form offers the correction from there with the typed figure
   * carried over, so this says what a correction is rather than how to find
   * the one to correct.
   */
  REPORTING_OPENING_BALANCE_ALREADY_RECORDED:
    'The park already has an opening balance, and it is never overwritten. Nothing was recorded. Correct the one that stands instead — the correction replaces it, and both are kept.',

  /**
   * Somebody corrected the same opening balance first (#521): the same race as
   * a superseded UPI declaration, and "reload" is again the part that matters —
   * the balance on screen is no longer the one the sheet starts from.
   */
  REPORTING_OPENING_BALANCE_SUPERSEDED:
    'Somebody corrected that opening balance while you were working on it, so yours was not recorded. Reload to see the one that stands now, then correct that one if it is still wrong.',

  /**
   * A sheet asked for from a day before the park's opening balance, or before
   * any has been recorded (#529). The API never answers with an assumed zero.
   *
   * **This is the code-only wording, and it names both cases** because the
   * code alone cannot tell them apart. `details.openingBalanceDate` can, and a
   * screen holding the refusal words it with
   * {@link messageForSheetBeforeOpeningBalance} instead.
   */
  REPORTING_DAILY_SHEET_BEFORE_OPENING_BALANCE:
    'The park’s sheet starts from its opening balance, and the period asked for begins before it — or no opening balance has been recorded yet. Start the period on or after that day, or record an opening balance first.',

  /**
   * A park expense line corrected by somebody else first (#529): the line on
   * screen is no longer the live one, so correcting it again would fork it.
   */
  REPORTING_PARK_EXPENSE_SUPERSEDED:
    'That line was already corrected; refresh and correct the current one.',

  RESOURCE_NOT_FOUND:
    'That record no longer exists, or was never there. Go back and refresh the list.',

  // ── Billing machines and their imports (blossom-rest-api epic #480) ─────────
  //
  // Every one of these is non-retryable, so none invites the same attempt again.
  // The per-bill codes arrive on `bills[].code` inside a `201` and name one bill,
  // not the upload; the file's other bills went in. Their `detail` echoes cells
  // from the file verbatim — a screen shows those beside this copy, as text — and
  // this copy never quotes them, because the code is all it is keyed on.

  /**
   * `machineCode` is text: `0946014363` and `946014363` are two machines, so a
   * person who "retyped the same number" may not have. Says to check the digits
   * before assuming the machine is someone else's.
   */
  MACHINE_CODE_TAKEN:
    'A machine with that machine ID is already registered. Check the ID against the machine — leading zeros count — and open the existing machine rather than registering it again.',

  /**
   * An attachment dated earlier than bills already imported from the machine
   * would move those bills to another outlet after the fact, so the API refuses
   * it rather than rewriting booked takings.
   */
  MACHINE_ATTACHMENT_BEHIND_IMPORTED_BILLS:
    'Bills from this machine have already been imported on or after that date, and moving it would change which outlet took that money. Nothing was changed. Choose a start date after the last imported bill.',

  MACHINE_PRODUCT_ITEM_RETIRED:
    'That catalogue item has been retired, so a product code cannot be mapped to it. Nothing was changed. Choose an item that is still on sale.',

  MACHINE_IMPORT_FILE_UNREADABLE:
    'That file could not be read as a machine export — it is not plain text, or it has no bills in it. Nothing was imported. Export ORDERS.CSV from the machine again and upload that file unchanged.',

  /**
   * Usually a file opened and re-saved in a spreadsheet, which reorders or
   * renames columns. The refusal carries `details.expected` and `details.found`
   * for a screen to show; the copy says to use the machine's file as exported.
   */
  MACHINE_IMPORT_HEADER_UNRECOGNISED:
    'The columns in that file are not the ones a machine export has. Nothing was imported. Upload ORDERS.CSV exactly as the machine produced it — a file opened and saved again in a spreadsheet often changes its columns.',

  /** The bound is 5,000 data rows (lowered from 10,000 by the API's T015). */
  MACHINE_IMPORT_TOO_MANY_ROWS:
    'That file has more than the 5,000 rows one upload accepts. Nothing was imported. Export a shorter period from the machine and upload each part separately.',

  /** `details.rows` names each unreadable row (the first 100); a screen lists them. */
  MACHINE_IMPORT_ROWS_UNREADABLE:
    'Some rows in that file could not be read, so none of it was imported. The rows are listed with the reason for each; export the file from the machine again, and report it if the same rows fail.',

  MACHINE_NOT_REGISTERED:
    'This bill comes from a machine ID that is not registered. Register the machine, then upload the file again — bills already imported will not be counted twice.',

  /**
   * A bill is booked to the outlet the machine was attached to on the bill's own
   * date, so a gap in the attachment history leaves it nowhere to go. Attaching
   * the machine is an owner's act.
   */
  MACHINE_NOT_ATTACHED_ON_DATE:
    'The machine was not attached to any outlet on this bill’s date, so there is no outlet to book it to. An owner can attach the machine from that date; then upload the file again.',

  /** `detail.productCode` names the code; mapping is open to owners and managers. */
  MACHINE_PRODUCT_CODE_UNMAPPED:
    'This bill has a product code that is not mapped to a catalogue item for its machine. Map the code, then upload the file again — bills already imported will not be counted twice.',

  /**
   * The printed name (`detail.productName`) differs from the one recorded with
   * the mapping (`detail.mappedName`): the machine has probably been
   * reprogrammed, and the code may now mean a different ride.
   */
  MACHINE_PRODUCT_CODE_REPROGRAMMED:
    'This bill’s product name does not match the name the code was mapped under, so the machine may have been reprogrammed. Check what the code now sells, map it again with the name exactly as the machine prints it, then upload the file again.',

  /**
   * `detail.saleId` names the bill already on the books. Reversing it and
   * uploading again imports the new contents as the next revision.
   */
  MACHINE_BILL_DIFFERS:
    'A bill with this receipt number and date was already imported with different contents, so this one was not recorded. Compare it with the imported bill; if that one is wrong, reverse it and upload the file again.',

  MACHINE_BILL_DATE_IN_FUTURE:
    'This bill is dated after the day the park is currently on, so it cannot be booked yet. Check the machine’s date and time setting; the bill can be uploaded once its date arrives.',

  /** `detail.column` and `detail.value` name the cell. */
  MACHINE_BILL_AMOUNT_INVALID:
    'An amount or quantity on this bill could not be read, or does not equal rate times quantity, so it was not recorded. Check the named cell against the machine’s own print-out, and report it if the machine printed it that way.',

  MACHINE_BILL_INCONSISTENT:
    'The rows of this bill disagree with each other on something every row of one bill must share, so it was not recorded. Export the file from the machine again; if the bill still disagrees, report it.',

  /**
   * No longer produced since blossom-rest-api#510, which imports discounted
   * lines. It still reaches a person from an import recorded before then, and
   * for that bill the way out is now simply to upload the file again.
   */
  MACHINE_BILL_DISCOUNT_UNSUPPORTED:
    'This bill carries a discount, and when this file was uploaded discounted machine bills were not accepted, so it was not recorded. They are accepted now: upload the file again and it will be imported — bills already imported will not be counted twice.',

  /** `detail.row` and `detail.value` name the line and its discount cell, as printed. */
  MACHINE_BILL_DISCOUNT_EXCEEDS_AMOUNT:
    'A line on this bill has a discount larger than the line amount, so the bill was not recorded. Check the named line against the machine’s own print-out, and report it if the machine printed it that way.',

  /**
   * A `BILL STATUS` or `TYPE` the park has not defined yet (CL-1101, CL-1102).
   * Not the person's mistake and not fixable from the file.
   */
  MACHINE_BILL_VALUE_UNRECOGNISED:
    'This bill has a status or payment type the system does not handle yet — for example a cancelled bill, or one not paid in cash. It was not recorded. Report it with the receipt number so it can be settled.',

  MACHINE_BILL_ALREADY_REVERSED:
    'That imported bill has already been reversed. Nothing more was changed. If it has to go back on the books, upload the file again and it will be imported as a new revision.',

  MACHINE_BILL_REVERSAL_REASON_INVALID:
    'A reversal needs a written reason of up to 500 characters. Nothing was changed. Write why this bill should not have been imported, then submit the reversal.',

  /**
   * Published by three routes with two bounds: the two machine-import uploads
   * (2 MiB) and the expense attachment upload (5 MiB, #513). One code, so one
   * sentence naming both. `messageForApiError` also answers a **codeless** 413
   * with this: nginx refuses a body over 10 MiB before the API sees it, with
   * an HTML page and no envelope.
   */
  VALIDATION_PAYLOAD_TOO_LARGE:
    'That file is too large, so none of it was read. A receipt or invoice can be at most 5 MB — take the photo at a smaller size or attach a smaller PDF. A machine export can be at most 2 MB — export a shorter period and upload each part separately.',

  // ───────────────────────────────────────────────────────────────────────────
  // Codes deliberately left to the generic fallback.
  //
  // Each carries the argument for leaving it, and the argument is a required
  // argument rather than a comment somebody may drop. The common thread: the
  // fallback states that the server refused and quotes the code to read down the
  // phone, which is the *honest* answer for a fault and the wrong one for a
  // refusal that states a rule. Everything below is the former.
  //
  // Reachability claims here are dated to the served document at 23 Aug 2026 and
  // go stale — re-check `openapi.json` before repeating one.
  // ───────────────────────────────────────────────────────────────────────────

  VALIDATION_FAILED: DELIBERATELY_GENERIC(
    'A malformed request rather than a refused one, and this map cannot say anything useful ' +
      'about it: the refusal names the offending fields in `details.fields`, and one sentence ' +
      'keyed on the code alone could only be vaguer than the fallback. Every form in this app ' +
      'validates with zod before it submits, so reaching this means the screen and the server ' +
      'disagree about a rule — a fault, and the fallback tells the truth about a fault. A ' +
      'screen holding the response should read `details.fields` and set the error on the field.',
  ),

  VALIDATION_UNKNOWN_FIELD: DELIBERATELY_GENERIC(
    'Unreachable from this client, and that is the decision rather than a guess: every request ' +
      'body is built from a generated operation type, so a field the endpoint does not define ' +
      'cannot be sent without a hand-written shape, which CLAUDE.md forbids. If it ever appears ' +
      'it is a fault in the client and the fallback says so correctly.',
  ),

  LEDGER_ENTRY_IMMUTABLE: DELIBERATELY_GENERIC(
    'No route in the served document publishes it, and no route anywhere modifies or deletes a ' +
      'ledger entry — the ledger is append-only by design and corrections are further entries. ' +
      'So there is no act a person could be performing when this arrives, and nothing to tell ' +
      'them to do differently. Deliberately unlike `LEDGER_AMOUNT_INVALID` beside it, which is ' +
      'published by four routes including a back-office form and therefore has copy.',
  ),

  TICKETING_NUMBER_CONFLICT: DELIBERATELY_GENERIC(
    'Not reachable by a well-formed client: ticket numbers are drawn from the terminal’s own ' +
      'sequence, and the same ticket resubmitted is answered 200 with its original outcome, so ' +
      'two baskets claiming one number means the sequence itself is wrong. Nothing a person can ' +
      'do, and the fallback’s "quote this code when you report it" is the correct instruction. ' +
      'Noted for the reviewer: `SALES_NUMBER_CONFLICT` is the same situation on receipts and ' +
      'does have copy, so this asymmetry is deliberate here but worth revisiting as one pair.',
  ),

  TICKETING_TICKET_IMMUTABLE: DELIBERATELY_GENERIC(
    'The same client fault as `SALES_SALE_IMMUTABLE` one epic earlier — a ticket id claimed by ' +
      'two different admissions — but reached only by a till whose id minting is broken, which ' +
      'the gate screens cannot cause. It is a fault with nothing for the person to do; the ' +
      'fallback quotes the code, which is what a support call needs.',
  ),

  TICKETING_TICKET_ENTRY_ALREADY_REVERSED: DELIBERATELY_GENERIC(
    'No route in the served document publishes it. A second void of one ticket is answered by ' +
      '`TICKETING_VOID_IMMUTABLE` or by the void that already stands, so this is the ledger ' +
      'half of a condition a client meets under another code first. If it surfaces, the two ' +
      'halves have disagreed and that is a fault rather than anything a person did.',
  ),

  SYSTEM_REQUEST_REJECTED: DELIBERATELY_GENERIC(
    'Defined as "the request was rejected and no more specific code applies", so the fallback ' +
      'is not a stand-in for copy here — it is the accurate statement, and any sentence this ' +
      'map invented would claim to know something the API has explicitly said it does not. No ' +
      'route in the served document publishes it.',
  ),

  SYSTEM_UNAVAILABLE: DELIBERATELY_GENERIC(
    'A dependency of the API is unreachable: a fault, and nothing the person in front of the ' +
      'screen chose or can change. Retryable, but the retrying is the sync engine’s job and ' +
      'not an instruction to a cashier — a till treats an unreachable server as an operating ' +
      'mode rather than an error (Principle V), and that is handled where the queue is, not in ' +
      'refusal copy. The fallback quotes the code, which is what a support call needs.',
  ),

  SYSTEM_INTERNAL_ERROR: DELIBERATELY_GENERIC(
    'An unexpected failure with the detail in the API’s logs and nothing about it in the ' +
      'envelope. It is the one code where "report this" is not a euphemism but the literal ' +
      'correct action, and the fallback’s quoted code is exactly what the person reporting it ' +
      'needs to read out.',
  ),
} satisfies Record<ErrorCode, string | GenericFallback>;

/**
 * Every code left to the fallback, with the argument for leaving it.
 *
 * Exported for `error-messages.test.ts`, which asserts what a type cannot: that
 * each reason says something, and that the code it is recorded against really
 * does fall through. It is also the honest answer to "what does this app decline
 * to explain, and why" — a question that previously had no answer short of
 * reading the map against the registry by hand.
 */
export const genericFallbackReasons: Readonly<Partial<Record<ErrorCode, string>>> = Object.freeze(
  Object.fromEntries(
    Object.entries(MESSAGES).flatMap(([code, entry]) =>
      typeof entry === 'string' ? [] : [[code, entry.genericBecause] as const],
    ),
  ),
);

/**
 * The shape of a code the API could actually have sent.
 *
 * The code arrives on the wire, so it is chosen by whatever answered — a
 * captive portal, a proxy, a hostile intermediary. An unmapped code is quoted
 * back to a staff member who reads it down the phone to support, and quoting
 * arbitrary text there turns the error panel into a channel for prose the API
 * never wrote ("call this number for your refund"). Only something shaped like
 * a registry key is repeated.
 */
const CODE_SHAPE = /^[A-Z][A-Z0-9_]{0,63}$/;

/**
 * What the fallbacks say. Neither guesses: one refusal was explained in a
 * vocabulary this client does not know yet, and the other was not explained at
 * all. "Something went wrong" is a lie in both cases — nothing went wrong, the
 * request was refused — and a blank is worse than either.
 */
const REFUSED_WITH_CODE = (code: string): string =>
  `The server refused this request and gave the code ${code}. Quote that code when you report it.`;

const REFUSED_WITHOUT_REASON =
  'The request was refused, and no reason came back with it. If it keeps happening, report it.';

/**
 * The message for a code, or an honest fallback where there is none.
 *
 * Takes a widened `string` rather than `ErrorCode`: the code on the wire comes
 * from the deployed API, which may be newer than this client, and a lookup that
 * cannot miss is a lookup that has not thought about what happens when it does.
 */
export function messageForErrorCode(code: string | undefined): string {
  if (code === undefined) return REFUSED_WITHOUT_REASON;

  // Only this map's own rows may answer. `MESSAGES['constructor']` resolves up
  // the prototype chain to a function rather than missing, and a screen then
  // renders "function Object() { [native code] }" at a till.
  //
  // A row is now either a sentence or a recorded decision to say nothing, and
  // only the first may be rendered — the `typeof` is what keeps a
  // `DELIBERATELY_GENERIC` from reaching a screen as "[object Object]".
  if (Object.hasOwn(MESSAGES, code)) {
    const entry = MESSAGES[code as ErrorCode];
    if (typeof entry === 'string') return entry;
  }

  return CODE_SHAPE.test(code) ? REFUSED_WITH_CODE(code) : REFUSED_WITHOUT_REASON;
}

/**
 * `REPORTING_DAILY_SHEET_BEFORE_OPENING_BALANCE`, worded from its
 * `details.openingBalanceDate` — the one refusal here whose sentence needs a
 * value from `details`, which {@link messageForApiError} deliberately never
 * reads.
 *
 * Here rather than on the screen so the copy for the code stays in this file.
 * Takes the day already formatted for reading: how a date is shown is the
 * app's business, and this package has no opinion on it.
 *
 * @param startsOn the first day the sheet answers, formatted; `null` when the
 *   park has no opening balance at all.
 */
export function messageForSheetBeforeOpeningBalance(startsOn: string | null): string {
  return startsOn === null
    ? 'Record the park’s opening balance to start the sheet.'
    : `The park’s sheet starts on ${startsOn}.`;
}

/**
 * The message for a refusal from the API.
 *
 * Reads `code` and nothing else. Not `message` — that is the API's prose for
 * humans and logs, which Constitution III says clients must not parse and which
 * for `AUTH_CREDENTIALS_INVALID` is the very thing that separates the three
 * situations the code deliberately merges. Not `details`, for the same reason.
 * Not `status`, which no person should ever be shown — except a codeless 413,
 * below.
 */
export function messageForApiError(error: ApiError): string {
  // The one status read here: a 413 above 10 MiB is nginx's own HTML page, with
  // no code to read, and it means the same thing the coded one does.
  if (error.code === undefined && error.status === 413) {
    return messageForErrorCode('VALIDATION_PAYLOAD_TOO_LARGE');
  }

  return messageForErrorCode(error.code);
}
